import React from 'react'
import {FormattedDate} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {isValid} from 'date-fns/isValid'

import {StartIconsWrapper} from '@vio/atlas/helpers/InputBase'
import {
  InputButton,
  InputButtonStyledContainer
} from '@vio/atlas/helpers/InputButton'
import {Icon} from '@vio/atlas/Icon'
import {truncateSingleLine} from '@vio/atlas/src/utils/truncate'
import {cssTheme} from '@vio/atlas/themes'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {MiddayDate} from '@vio/core/src/utils/date'

import {guestPickerMessages} from '../GuestPicker/guestPickerMessages'

interface Props {
  /** Check in date */
  checkIn: MiddayDate
  /** Check out date */
  checkOut: MiddayDate
  /** Destination name */
  destination: React.ReactNode
  /** Number of guests */
  numberOfGuests: number
  /** Number of rooms */
  numberOfRooms: number
  /** Click callback */
  onClick: () => void
  /** Whether to show the compacted variant */
  isCompact?: boolean
  /** Whether the rooms count and label is shown */
  isRoomsLabelShown?: boolean
  /** Whether the Summary Button is rounded */
  isRounded?: boolean
  /** Whether the color used for borders and icons is the default or neutral */
  isNeutralColor?: boolean
  /** When true, hides the check-in/check-out dates (used for dateless SEO SRP pages) */
  isDatelessSeoSRP?: boolean
}

const NEW_SEARCH_SUMMARY_HEIGHT = 56
const NEW_SEARCH_SUMMARY_COMPACT_HEIGHT = 48

const SummaryButton = styled(InputButton)`
  border-radius: ${cssTheme.layout.radius.rounded};
  box-shadow: ${cssTheme.shadows.fixed};
  height: ${NEW_SEARCH_SUMMARY_HEIGHT}px;
  &.--is-compact {
    height: ${NEW_SEARCH_SUMMARY_COMPACT_HEIGHT}px;
    ${StartIconsWrapper} {
      height: ${NEW_SEARCH_SUMMARY_COMPACT_HEIGHT}px;
    }
  }
  &:not(.--is-neutral-color) {
    ${InputButtonStyledContainer} {
      border-color: ${cssTheme.colors.srp.searchBox.border};
      border-width: 1.5px;
    }
  }
  ${StartIconsWrapper} {
    height: ${NEW_SEARCH_SUMMARY_HEIGHT}px;
  }
`

const Wrapper = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  text-align: left;
  min-width: 0;
  padding-right: ${cssTheme.layout.spacing.s400};
  &.--is-compact {
    flex-direction: row;
    align-items: center;
  }
`

const DestinationText = styled.span`
  ${cssTheme.typography.labelM};

  ${truncateSingleLine()}
  &.--is-compact {
    ${cssTheme.typography.labelS};
  }
`

const GuestsDateWrapper = styled.span`
  ${cssTheme.typography.bodyXS};
  white-space: nowrap;

  &.--is-compact {
    margin-top: ${cssTheme.layout.spacing.s100};
  }
`

const CompactBullet = styled.span`
  ${cssTheme.typography.labelS};
`

const ShortFormattedDate = ({value}: {value: Date}): React.ReactElement => (
  <FormattedDate value={value} month="short" day="2-digit" timeZone="UTC" />
)

export const SearchSummaryButton = ({
  destination,
  checkIn,
  checkOut,
  numberOfGuests,
  numberOfRooms,
  onClick,
  isCompact = false,
  isRoomsLabelShown = true,
  isRounded = true,
  isNeutralColor = false,
  isDatelessSeoSRP
}: Props) => {
  const isDatesValid = isValid(checkIn) && isValid(checkOut)

  return (
    <SummaryButton
      id="SearchSummary"
      onClick={onClick}
      dataId="SearchSummary"
      className={cx(
        isCompact && '--is-compact',
        isNeutralColor && '--is-neutral-color'
      )}
      icon={
        <Icon
          name="Search"
          size="md"
          colorPath={isNeutralColor ? undefined : 'content.interactive.primary'}
        />
      }
      isRounded={isRounded}
    >
      <Wrapper className={cx(isCompact && '--is-compact')}>
        <DestinationText
          data-id="DestinationValue"
          className={cx(isCompact && '--is-compact')}
          dir="auto"
        >
          {destination || (
            <FormattedMessageWrapper
              id="whereAreYouGoing"
              defaultMessage="Where are you going?"
            />
          )}
        </DestinationText>

        {isCompact && <CompactBullet>{'\u00A0·\u00A0'}</CompactBullet>}

        {isDatesValid && (
          <GuestsDateWrapper
            className={cx(isCompact && '--is-compact', 'chromatic-ignore')}
          >
            <>
              {isDatelessSeoSRP ? (
                <FormattedMessageWrapper
                  id="addDates"
                  defaultMessage="Add dates"
                />
              ) : (
                <>
                  <span data-id="CheckInValue">
                    <ShortFormattedDate value={checkIn} />
                  </span>
                  {' - '}
                  <span data-id="CheckOutValue">
                    <ShortFormattedDate value={checkOut} />
                  </span>
                </>
              )}
            </>
            {!isCompact && (
              <>
                <span data-id="GuestValue">{' · '}</span>
                <FormattedMessageWrapper
                  {...guestPickerMessages.guest}
                  values={{count: numberOfGuests}}
                />
                {isRoomsLabelShown && (
                  <>
                    <span data-id="RoomValue">{', '}</span>
                    <FormattedMessageWrapper
                      {...guestPickerMessages.room}
                      values={{count: numberOfRooms}}
                    />
                  </>
                )}
              </>
            )}
          </GuestsDateWrapper>
        )}
      </Wrapper>
    </SummaryButton>
  )
}
